"use client"

import { useTranslations } from "next-intl";;

import { useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { AlertTriangle, Bookmark, ChevronLeft, Clock, Info, MapPin, Upload } from "lucide-react";
import { getSupabaseBrowserClient } from "@/utils/supabase/client";
import { BookmarkLoginModal, type BookmarkModalListing } from "@/components/search/product/bookmark-login-modal";
import { useSavedState } from "@/hooks/use-saved-state";
import { UnifiedAddToCartButton } from "@/components/UnifiedAddToCartButton";
import { MakeOfferButton } from "@/components/offers/make-offer-button";
import { ListingSplitCommerceCta } from "@/components/offers/listing-split-commerce-cta";
import { ListingOffersTrigger } from "@/components/offers/listing-offers-trigger";
import { ListingOwnerEditButton } from "@/components/detailed-listing-view/listing-owner-edit-button";
import { OrderPartyCard } from "@/components/orders/order-party-card";
import { SellerCheckoutNoteBanner } from "@/components/cart/seller-checkout-note-banner";
import { cn, formatPrice } from "@/lib/utils";
import { useScheduledCountdown } from "@/hooks/use-scheduled-countdown";
import { ShareSheet } from "@/components/share/share-sheet";
import type { ListingShareMeta } from "@/lib/story-cards";
import { ReportMenu } from "@/components/ui/report-menu";
import MobileListingGallery from "@/components/detailed-listing-view/mobile-listing-gallery"
import { AuctionPanelFromContext, type AuctionData } from "@/components/detailed-listing-view/auction-panel"
import { useViewerAuctionState } from "@/hooks/use-viewer-auction-state"
import { usePaymentDeadline } from "@/hooks/use-payment-deadline"
import type { ViewerAuctionBaseline } from "@/lib/auction-viewer-state"
import { Button } from "@/components/ui/button"
import { throttle } from "@/lib/utils/throttle"
import { TcgCardDetails, type TcgCardDetailsData } from "@/components/detailed-listing-view/tcg-card-details"
import { PreorderTimelineSection } from "@/components/listings/preorder-timeline-section"
import { HUB_OVERLAY_BACKDROP, HUB_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop"

// Inert baseline used when the page is rendering a fixed-price (non-auction)
// listing. The hook is always called for stable hook order, but downstream
// usage of `viewerState` is gated on `auction != null`.
const STUB_AUCTION_BASELINE: ViewerAuctionBaseline = {
  startPrice: 0,
  reservePrice: null,
  endAt: null,
  status: "live",
  highestBid: null,
  highestBidderId: null,
  bidCount: 0,
  paymentDeadline: null,
}

function MobilePaymentDeadline({ deadline, onExpired }: { deadline: string; onExpired?: () => void }) {
  const state = usePaymentDeadline(deadline, onExpired)
  if (!state) return null
  const isUrgent = !state.expired && state.hours < 4
  return (
    <div className={`flex items-center gap-2 text-sm ${state.expired ? "text-red-400" : isUrgent ? "text-amber-300" : "text-neutral-400"}`}>
      <Info className="h-3.5 w-3.5 shrink-0" />
      <span>{state.label}</span>
    </div>
  )
}

const ListingSuggestionsLazy = dynamic(
  () => import("@/components/detailed-listing-view/listing-suggestions").then((m) => ({ default: m.ListingSuggestions })),
  { ssr: false }
)

interface MobileProductPageProps {
  title?: string;
  price?: string;
  currency?: string | null;
  condition?: string;
  grading?: string;
  description?: string;
  images?: string[];
  itemId?: string;
  location?: string;
  createdAt?: string;
  seller?: {
    id: string;
    username: string;
    avatar_url?: string;
    followers_count: number;
    is_following: boolean;
    sales_count?: number;
    checkout_note?: string | null;
  };
  currentUserId?: string;
  stock?: number;
  isSaved?: boolean;
  savesCount?: number;
  scheduledPublishAt?: string | null;
  sellerBanned?: boolean;
  isUnlisted?: boolean;
  auction?: AuctionData | null;
  onPayAsWinner?: () => void;
  onBack?: () => void;
  auctionCountryBanWarning?: string | null;
  cardDetails?: TcgCardDetailsData | null;
  listingMeta?: ListingShareMeta;
  isPreorder?: boolean;
  listingFormat?: string | null;
  // Phase 3 (2026-06-29): `acceptsOffers` dropped — derive from `listingFormat`.
  autoDeclineBelow?: number | null;
}



const gradingColor = (grade: string) => {
  const n = Number(grade);
  if (!Number.isFinite(n)) return "bg-white/20";
  if (n >= 9) return "bg-emerald-500";
  if (n >= 7) return "bg-amber-500";
  return "bg-orange-500";
};


export default function MobileProductPage({
  title = "No title",
  price = "0",
  currency,
  condition = "unknown",
  grading = "N/A",
  description = "",
  images = [],
  itemId = "",
  location = "",
  createdAt = "",
  seller,
  currentUserId,
  stock,
  isSaved = false,
  savesCount: initialSavesCount,
  scheduledPublishAt = null,
  sellerBanned = false,
  isUnlisted = false,
  auction = null,
  onPayAsWinner,
  onBack,
  auctionCountryBanWarning = null,
  cardDetails = null,
  listingMeta,
  isPreorder = false,
  listingFormat = null,
  autoDeclineBelow = null,
}: MobileProductPageProps) {
  const router = useRouter();
  const tListing = useTranslations("listing");

  // Single source of truth for the viewer projection — see issue #156.
  // Uses a stub baseline when no auction is present (page also renders fixed-
  // price listings); downstream usage is gated on `auction != null`.
  const viewerState = useViewerAuctionState({
    listingId: itemId ?? "",
    currentUserId: currentUserId ?? null,
    auction: auction ?? STUB_AUCTION_BASELINE,
    sellerId: seller?.id ?? "",
  });

  const formatRelativeTime = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (diff < 60) return tListing("relativeTime.justNow");
    if (diff < 3600) return tListing("relativeTime.minutesAgo", { count: Math.floor(diff / 60) });
    if (diff < 86400) return tListing("relativeTime.hoursAgo", { count: Math.floor(diff / 3600) });
    if (diff < 2592000) return tListing("relativeTime.daysAgo", { count: Math.floor(diff / 86400) });
    if (diff < 31536000) return tListing("relativeTime.monthsAgo", { count: Math.floor(diff / 2592000) });
    return tListing("relativeTime.yearsAgo", { count: Math.floor(diff / 31536000) });
  };


  const { isScheduled } = useScheduledCountdown(scheduledPublishAt);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [savesCount, setSavesCount] = useState<number>(initialSavesCount ?? 0);

  const isAuctionEnded =
    auction != null && viewerState.kind !== "live" && viewerState.kind !== "scheduled";
  const isAuctionSeller = viewerState.kind === "ended_seller" ||
    Boolean(auction && seller && currentUserId === seller.id);
  const isOwner = Boolean(currentUserId && seller && currentUserId === seller.id);
  const showAuctionBidBar = Boolean(auction && !isAuctionEnded && !isAuctionSeller);
  const isWinnerSettled = viewerState.kind === "ended_won_settled";
  const isWinnerPending = viewerState.kind === "ended_won_pending_payment";
  const winnerDeadline = isWinnerPending ? viewerState.deadline : null;
  const isPaymentExpired = viewerState.kind === "ended_payment_expired";
  const showWinnerBar = isWinnerSettled || isWinnerPending || isPaymentExpired;

  const { saved, loading: saving, toggleSaved } = useSavedState({
    itemId,
    itemType: "listing",
    initialSaved: isSaved,
    currentUserId,
  });
  const titleSentinelRef = useRef<HTMLDivElement | null>(null);
  const titleBarRef = useRef<HTMLDivElement | null>(null);
  const [isTitlePinned, setIsTitlePinned] = useState(false);
  const [titleBarHeight, setTitleBarHeight] = useState(0);

  const listingForModal = useMemo<BookmarkModalListing>(
    () => ({
      id: itemId,
      title,
      price,
      currency,
      location,
      image_urls: images,
      description,
      condition,
      grade: grading,
      created_at: createdAt,
      username: seller?.username,
      avatar_url: seller?.avatar_url,
      scheduledPublishAt,
    }),
    [
      itemId,
      title,
      price,
      currency,
      location,
      images,
      description,
      condition,
      grading,
      createdAt,
      seller?.username,
      seller?.avatar_url,
      scheduledPublishAt,
    ],
  );

  useEffect(() => {
    if (initialSavesCount != null) {
      setSavesCount(initialSavesCount);
    } else {
      let active = true;
      const loadCount = async () => {
        try {
          if (!itemId) return;
          const supabase = getSupabaseBrowserClient();
          const { count, error } = await supabase
            .from("saved")
            .select("*", { count: "exact", head: true })
            .eq("item_type", "listing")
            .eq("item_id", itemId)
            .is("deleted_at", null);
          if (error) throw error;
          if (active) setSavesCount(count ?? 0);
        } catch {
          if (active) setSavesCount(0);
        }
      };
      loadCount();
      return () => {
        active = false;
      };
    }
  }, [itemId, initialSavesCount]);

  useEffect(() => {
    const rafRef = { current: 0 as number | 0 };
    const lastPinnedRef = { current: false };
    const lastHeightRef = { current: 0 };
    let measureHeightTimer: ReturnType<typeof setTimeout> | null = null;
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastUpdateTime = 0;

    const measureHeight = () => {
      const el = titleBarRef.current;
      if (!el) return;
      const nextHeight = el.getBoundingClientRect().height;
      if (nextHeight !== lastHeightRef.current) {
        lastHeightRef.current = nextHeight;
        setTitleBarHeight(nextHeight);
      }
    };

    const updatePinned = () => {
      const now = Date.now();
      if (now - lastUpdateTime < 50) return;
      lastUpdateTime = now;
      
      const sentinel = titleSentinelRef.current;
      if (!sentinel) return;
      const sentinelTop = sentinel.getBoundingClientRect().top;
      const nextPinned = sentinelTop <= 0;
      if (nextPinned !== lastPinnedRef.current) {
        lastPinnedRef.current = nextPinned;
        setIsTitlePinned(nextPinned);
      }
    };

    const schedule = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        if (rafRef.current) return;
        rafRef.current = window.requestAnimationFrame(() => {
          rafRef.current = 0;
          updatePinned();
        });
      }, 16);
    };

    const onResize = throttle(() => {
      if (measureHeightTimer) return;
      measureHeightTimer = setTimeout(() => {
        measureHeightTimer = null;
        measureHeight();
        schedule();
      }, 100);
    }, 100);

    const onScroll = throttle(() => {
      schedule();
    }, 16);

    measureHeight();
    updatePinned();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      if (measureHeightTimer) clearTimeout(measureHeightTimer);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (typeof window === "undefined") {
      router.push("/feed");
      return;
    }
    // Spec §E 1.6b / §G acceptance: search is a surface (route /search), not
    // an overlay on /feed — go straight back to it with the query intact.
    // `document.referrer` never changes across client-side (SPA) navigation,
    // so it can't tell us "came from search"; this flag is the explicit
    // signal set at tap time by the mobile /search item-card onClick
    // (unified-search-page-client.tsx) before pushing here. Landing on
    // /search re-syncs `submittedQuery` from the URL and
    // `usePaginatedFeed`'s `cacheKey: "search"` snapshot hydrates the results
    // + scroll position synchronously on mount — no skeleton, no /feed hop.
    const searchRestore = sessionStorage.getItem("kraahl_search_restore");
    if (searchRestore) {
      sessionStorage.removeItem("kraahl_search_restore");
      router.push(`/search?query=${encodeURIComponent(searchRestore)}`);
      return;
    }
    const referrer = document.referrer;
    if (!referrer) {
      router.push("/feed");
      return;
    }
    try {
      const referrerUrl = new URL(referrer);
      if (referrerUrl.origin !== window.location.origin) {
        router.push("/feed");
        return;
      }
    } catch {
      router.push("/feed");
      return;
    }
    if (window.history.length <= 1) {
      router.push("/feed");
      return;
    }
    router.back();
  };

  const handleSaveItem = async () => {
    if (!currentUserId) {
      setShowBookmarkModal(true);
      return;
    }
    const next = !saved;
    setSavesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      await toggleSaved();
    } catch {
      setSavesCount((c) => Math.max(0, c + (saved ? 1 : -1)));
    }
  };

  const country = useMemo(() => {
    if (!location || location.trim() === "") return "";
    const parts = location.split(",");
    return parts[parts.length - 1].trim();
  }, [location]);

  const formattedDate = useMemo(() => formatRelativeTime(createdAt), [createdAt]);
  const gradingColorMemo = useMemo(() => gradingColor(grading), [grading]);

  return (
    <div className="min-h-dvh w-full bg-background text-foreground">
      <BookmarkLoginModal
        open={showBookmarkModal}
        onClose={() => setShowBookmarkModal(false)}
        listing={listingForModal}
      />

      <MobileListingGallery images={images} title={title} isScheduled={isScheduled}>
        <div className="absolute left-3 top-3 z-30 flex items-center gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md"
            aria-label="Go back"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        </div>

        {itemId ? (
          <div className="absolute right-3 top-3 z-30">
            {isOwner ? (
              <ListingOwnerEditButton
                variant="header-icon-overlay"
                listingId={itemId}
                title={title}
                price={price}
                currency={currency}
                images={images}
                description={description}
                condition={condition}
                listingFormat={listingFormat}
              />
            ) : (
              <ReportMenu
                itemType="listing"
                itemId={itemId}
                itemTitle={title}
                currentUserId={currentUserId}
                reportedUserId={seller?.id}
                reportedUsername={seller?.username}
                reportedAvatarUrl={seller?.avatar_url ?? null}
                triggerIcon="menu"
                surface="glass"
                triggerClassName="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md"
                iconClassName="h-5 w-5"
              />
            )}
          </div>
        ) : null}

        <div className="absolute right-3 bottom-3 z-30">
          <ShareSheet
            url={`/listings/${itemId}`}
            title={title}
            image={images[0]}
            price={typeof price === "string" ? Number(price) : 0}
            currency={currency ?? undefined}
            storyData={seller?.username ? { seller: seller.username } : undefined}
            listingMeta={listingMeta}
            variant="item"
          >
            <button
              type="button"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md"
              aria-label="Share"
            >
              <Upload className="h-5 w-5" />
            </button>
          </ShareSheet>
        </div>
      </MobileListingGallery>

      <div className="px-4 pt-0 pb-30">
        <div ref={titleSentinelRef} />
        {isTitlePinned ? <div style={{ height: `${titleBarHeight}px` }} /> : null}
        <div
          ref={titleBarRef}
          className={
            isTitlePinned
              ? "fixed top-0 left-0 right-0 z-40 safe-area-top isolate"
              : "-mx-4"
          }
        >
          {isTitlePinned ? (
            <div aria-hidden className={HUB_OVERLAY_BACKDROP} style={HUB_OVERLAY_MASK} />
          ) : null}
          {/* px/py live on the row, not the bar: safe-area-top also sets
              padding-top, so a py-* on the same element gets overridden and the
              title hugs the status bar in standalone PWA. */}
          <div className="relative z-1 flex items-start justify-between gap-3 px-4 py-3">
            <div className="flex items-start gap-3 flex-1">
              {isTitlePinned ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center justify-center"
                  aria-label="Go back"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              ) : null}
              <h1 className="text-xl font-semibold leading-tight flex-1">{title}</h1>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={handleSaveItem}
                disabled={saving}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${
                  saved ? "bg-foreground text-background" : "bg-muted text-foreground"
                }`}
                aria-label={saved ? "Remove from saved" : "Save item"}
              >
                <Bookmark className={`h-5 w-5 ${saved ? "fill-current" : ""}`} />
              </button>
              {savesCount > 0 ? (
                <span className="absolute -bottom-1 -right-1 rounded-full bg-foreground px-1.5 py-0.5 text-[10px] font-semibold text-background">
                  {savesCount}
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {auction ? (
              isAuctionEnded ? (
                <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  {tListing("ended")}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium border border-green-500/30">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                  Live
                </span>
              )
            ) : null}
            {cardDetails ? (
              <TcgCardDetails card={cardDetails} />
            ) : (
              <>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize bg-muted text-foreground dark:bg-white/20 dark:text-white">
                  {condition}
                </span>
                {condition.toLowerCase() !== "new" && grading ? (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${gradingColorMemo === "bg-white/20" ? "bg-muted text-foreground dark:bg-white/20 dark:text-white" : `${gradingColorMemo} text-white`}`}>
                    {grading}/10
                  </span>
                ) : null}
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 text-sm text-muted-foreground">
            {country ? (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>{country}</span>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 shrink-0" />
              <span>{tListing("listed", { time: formattedDate })}</span>
            </div>
          </div>

          {seller ? (
            <OrderPartyCard
              role="seller"
              name={seller.username}
              username={seller.username}
              avatarUrl={seller.avatar_url ?? null}
              userId={seller.id}
              currentUserId={currentUserId}
              initialFollowing={Boolean(seller.is_following)}
              salesCount={seller.sales_count}
              followersCount={seller.followers_count}
              surface="plain"
              messageVariant="secondary"
              currentListing={itemId ? {
                id: itemId,
                title,
                price,
                image_urls: images,
                condition,
                grade: grading,
              } : null}
            />
          ) : null}

          <SellerCheckoutNoteBanner note={seller?.checkout_note ?? ""} />

          <div className="pt-2">
            <h2 className="text-base font-semibold">{tListing("description")}</h2>
            <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line wrap-break-word overflow-wrap-anywhere">
              {description}
            </p>
          </div>

          {isPreorder && itemId ? (
            <PreorderTimelineSection listingId={itemId} className="mt-0 pt-4" />
          ) : null}

          {auction && auctionCountryBanWarning ? (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-900/20 border border-amber-800/30">
              <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-200">{auctionCountryBanWarning}</p>
            </div>
          ) : null}

          {auction && seller ? (
            <div className="pt-4">
              <div className="rounded-3xl border border-border bg-card overflow-hidden">
                <AuctionPanelFromContext
                  listingId={itemId ?? ""}
                  auction={auction}
                  currency={currency}
                  currentUserId={currentUserId}
                  sellerId={seller.id}
                  onlyStats={true}
                  omitOuterSurface
                  onPayAsWinner={onPayAsWinner}
                />
                <AuctionPanelFromContext
                  listingId={itemId ?? ""}
                  auction={auction}
                  currency={currency}
                  currentUserId={currentUserId}
                  sellerId={seller.id}
                  onlyChat={true}
                  chatEmbedded
                />
              </div>
            </div>
          ) : null}

          {seller ? (
            <div className="pt-2">
              <ListingSuggestionsLazy
                currentListingId={itemId}
                sellerId={seller.id}
                sellerUsername={seller.username}
                currentUserId={currentUserId}
              />
            </div>
          ) : null}
        </div>
      </div>

      {auction && seller && showAuctionBidBar ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 rounded-t-3xl bg-black border-t border-neutral-900 p-4 space-y-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <AuctionPanelFromContext listingId={itemId ?? ""} auction={auction} currency={currency} currentUserId={currentUserId} sellerId={seller.id} onlyBidBar />
        </div>
      ) : null}
      {auction && seller && showWinnerBar ? (
        <div className="fixed bottom-0 left-0 right-0 z-40 rounded-t-3xl bg-black border-t border-neutral-900 p-4 space-y-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          {isWinnerSettled ? (
            <Button className="w-full rounded-full bg-neutral-800 text-neutral-400 cursor-default" size="lg" disabled>
              Paid {formatPrice(String(auction.highestBid ?? auction.startPrice), currency || "USD")}
            </Button>
          ) : isWinnerPending ? (
            <>
              {winnerDeadline && (
                <MobilePaymentDeadline deadline={winnerDeadline} />
              )}
              <Button className="w-full rounded-full bg-white text-black hover:bg-neutral-200" size="lg" onClick={() => onPayAsWinner?.()}>
                Pay {formatPrice(String(auction.highestBid ?? auction.startPrice), currency || "USD")}
              </Button>
            </>
          ) : (
            <p className="text-sm text-red-300 text-center">Payment deadline expired</p>
          )}
        </div>
      ) : null}
      {seller && !auction ? (() => {
        // Phase 3 (2026-06-29): "offers enabled" derives purely from format.
        const isOffersOnly = listingFormat === "offers_only"
        const offersAllowed =
          isOffersOnly || listingFormat === "fixed_price_with_offers"
        const canSplit =
          listingFormat === "fixed_price_with_offers" && !isOwner
        const showBuyNow = !isOffersOnly && !isOwner && !canSplit
        const showMakeOffer = offersAllowed && !isOwner && !canSplit
        const showSellerPanel = isOwner && offersAllowed
        const showOwnerFooter = !isOwner || showSellerPanel
        const priceClass = "shrink-0 text-xl font-bold leading-none tabular-nums text-foreground sm:text-2xl"
        void autoDeclineBelow
        if (!showOwnerFooter) return null
        return (
          <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/90 backdrop-blur-xl rounded-t-3xl shadow-[0_-12px_30px_rgba(0,0,0,0.25)]">
            <div className="flex flex-col gap-1.5 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:gap-2 sm:py-4">
              {!isOffersOnly && !isOwner && (
                <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                  <div className={cn(priceClass, canSplit && "flex h-11 min-h-11 shrink-0 items-center")}>
                    {formatPrice(price, currency || "USD")}
                  </div>
                  {canSplit && itemId ? (
                    <div className="min-w-0 flex-1">
                      <ListingSplitCommerceCta
                        unified={{
                          price,
                          currency,
                          listingId: itemId,
                          stock: stock,
                          currentUserId,
                          name: title,
                          images,
                          sellerId: seller.id,
                          sellerName: seller.username,
                          condition,
                          description,
                          shippingMethods: [],
                          context: "product-page",
                          lazy: false,
                          scheduledPublishAt,
                          sellerBanned,
                          isUnlisted,
                          listingType: isPreorder ? "preorder" : null,
                          splitBarCompact: true,
                        }}
                        offer={{
                          listingId: itemId,
                          currentUserId: currentUserId ?? null,
                          listingPrice: Number(price),
                          currency: currency ?? null,
                          listingTitle: title,
                          listingImageUrl: images[0] ?? null,
                        }}
                      />
                    </div>
                  ) : null}
                  {showBuyNow && itemId ? (
                    <div className="min-w-0 flex-1">
                      <UnifiedAddToCartButton
                        price={price}
                        currency={currency}
                        listingId={itemId}
                        stock={stock}
                        currentUserId={currentUserId}
                        name={title}
                        images={images}
                        sellerId={seller.id}
                        sellerName={seller.username}
                        condition={condition}
                        description={description}
                        shippingMethods={[]}
                        context="product-page"
                        lazy={false}
                        scheduledPublishAt={scheduledPublishAt}
                        sellerBanned={sellerBanned}
                        isUnlisted={isUnlisted}
                        listingType={isPreorder ? "preorder" : null}
                      />
                    </div>
                  ) : null}
                </div>
              )}
              {showMakeOffer && itemId ? (
                <MakeOfferButton
                  listingId={itemId}
                  currentUserId={currentUserId ?? null}
                  sellerId={seller.id}
                  listingFormat={listingFormat ?? "fixed_price"}
                  listingPrice={isOffersOnly ? null : Number(price)}
                  currency={currency ?? null}
                  listingTitle={title}
                  listingImageUrl={images[0] ?? null}
                  listingCondition={condition}
                  listingGrade={grading}
                  sellerUsername={seller.username}
                  fullWidth
                />
              ) : null}
              {showSellerPanel && itemId && currentUserId ? (
                <ListingOffersTrigger
                  listingId={itemId}
                  currentUserId={currentUserId}
                  currency={currency ?? null}
                />
              ) : null}
            </div>
          </div>
        )
      })() : null}

      {(auction && (showAuctionBidBar || showWinnerBar)) ? <div className="h-32" /> : null}
    </div>
  );
}
